import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Send, Sunrise, Moon, Copy, Check } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { useMenu } from '@/hooks/useMenu';
import { useUiStore } from '@/store/ui.store';
import { cn } from '@/lib/cn';
import { apiFetch } from '@/api/client';
import * as asuntosApi from '@/api/asuntos.api';
import * as inspectorApi from '@/api/inspector.api';

/**
 * ATHENA — conversación de verdad.
 *
 * Esteban, 20-sep: *"la quiero viva, conectada, que yo pueda hablar con ella
 * como una persona común y corriente, como un agente normal."*
 *
 * Antes eran cuatro botones con respuestas calculadas. Ahora es un chat con el
 * modelo, que ve el estado del día completo.
 *
 * ═══ VIVE DENTRO DE UNA VENTANA ═══
 *
 * Esto es el cuerpo de `VentanaAthena`, no una página: ocupa el alto que la
 * ventana le dé y el título lo pone ella.
 *
 * ═══ NO HAY BOTÓN DE ENVIAR A WHATSAPP ═══
 *
 * Athena redacta; mandar lo hace el dueño. Cada respuesta trae "Copiar", y eso
 * es todo lo que la app hace con un texto. La regla no se sostiene con
 * disciplina: no existe la ruta para enviar.
 *
 * ═══ EL HISTORIAL VIVE EN LA PANTALLA ═══
 *
 * No se guarda en la base. Es una charla de trabajo sobre datos que ya están
 * guardados; guardarla crearía una segunda copia de la vida del dueño, con las
 * mismas cosas dichas de otra forma, y después hay que decidir cuál manda.
 */

interface Turno {
  rol: 'usuario' | 'athena';
  texto: string;
}

const SUGERENCIAS = [
  '¿Qué tengo hoy?',
  '¿Qué se me está escapando?',
  '¿Quién me debe?',
  'Prepárame para mi próxima reunión',
  'Redáctame un mensaje para cobrarle a Carlos',
];

function Burbuja({ t, onCopiar }: { t: Turno; onCopiar: (texto: string) => void }) {
  const mio = t.rol === 'usuario';
  const [copiado, setCopiado] = useState(false);

  return (
    <div className={cn('flex', mio ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm',
          mio
            ? 'bg-violet-600 text-white'
            : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100',
        )}
      >
        <p className="whitespace-pre-wrap break-words">{t.texto}</p>
        {/* "Copiar" y nada más. Es todo lo que la app hace con un texto que
            Athena redacta: mandarlo es del dueño. */}
        {!mio && (
          <button
            onClick={() => {
              onCopiar(t.texto);
              setCopiado(true);
              setTimeout(() => setCopiado(false), 1500);
            }}
            className="mt-1.5 flex min-h-[32px] items-center gap-1 text-xs text-slate-500 dark:text-slate-400"
          >
            {copiado ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copiado ? 'Copiado' : 'Copiar'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function Athena() {
  const [turnos, setTurnos] = useState<Turno[]>([]);
  const [texto, setTexto] = useState('');
  const fondo = useRef<HTMLDivElement | null>(null);
  const toast = useToast();
  const navegar = useNavigate();
  const cerrar = useUiStore((s) => s.cerrarAthena);

  // ═══ SI EL NEGOCIO NO LA TIENE, SE DICE ═══
  //
  // El botón de Athena sale en todas las pantallas, pero sus rutas son de un
  // módulo: apagado, la API responde 404 a todo. Antes eso se veía como
  // «Tienes 0 cosas abiertas. Nada vencido» y un «No pude responder» al
  // preguntar: un cero inventado y un fallo sin causa. El menú del servidor
  // dice si este negocio la tiene; si no, se explica y no se pide nada.
  const { datos: menu } = useMenu();
  const apagada = Boolean(menu) && !menu!.rutas.some((r) => r.ruta === '/athena');
  const encendida = Boolean(menu) && !apagada;

  const asuntos = useQuery({
    queryKey: ['asuntos', 'athena'],
    queryFn: () => asuntosApi.getAsuntos({ limite: 300 }),
    enabled: encendida,
  });
  const senales = useQuery({
    queryKey: ['inspector', 'senales'],
    queryFn: inspectorApi.getSenales,
    enabled: encendida,
  });

  const preguntar = useMutation({
    mutationFn: (pregunta: string) =>
      apiFetch<{ texto: string; noPudeVer: string[] }>('/api/athena/preguntar', {
        method: 'POST',
        body: { pregunta, historial: turnos.slice(-12) },
      }),
    onSuccess: (r) => setTurnos((t) => [...t, { rol: 'athena', texto: r.texto }]),
    onError: (e: Error) => {
      toast.error(e.message);
      // El turno del usuario se queda: borrarlo le haría reescribir la
      // pregunta por un fallo que no fue suyo.
      setTurnos((t) => [...t, { rol: 'athena', texto: 'No pude responder. Vuelve a intentar.' }]);
    },
  });

  useEffect(() => {
    fondo.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [turnos, preguntar.isPending]);

  const enviar = (pregunta: string) => {
    const limpia = pregunta.trim();
    if (!limpia || preguntar.isPending) return;
    setTurnos((t) => [...t, { rol: 'usuario', texto: limpia }]);
    setTexto('');
    preguntar.mutate(limpia);
  };

  /** Ir a una pantalla del panel cierra la ventana: si no, la tapa. */
  const irA = (ruta: string) => {
    cerrar();
    navegar(ruta);
  };

  if (apagada) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-4 text-center">
        <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
          Athena no está encendida en este negocio.
        </p>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Por eso no puede leer tus chats ni responderte todavía.
        </p>
        <button
          onClick={() => irA('/ajustes/modulos')}
          className="min-h-[44px] rounded-lg bg-violet-600 px-4 text-sm font-medium text-white hover:bg-violet-700"
        >
          Ver qué sabe hacer este negocio
        </button>
      </div>
    );
  }

  const lista = asuntos.data?.asuntos ?? [];
  const vencidos = lista.filter((a) => a.vencido).length;
  const cuantasSenales = senales.data?.senales.length ?? 0;
  const ahora = new Date();

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {/* EL BRIEFING, mientras no haya conversación: es lo que ella sabe sin
          que le preguntes. Al primer mensaje se va, para dejarle sitio al hilo. */}
      {turnos.length === 0 && (
        <div className="grid shrink-0 gap-2">
          <Card>
            <CardBody>
              <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200">
                <Sunrise className="h-4 w-4" /> Tu mañana
              </p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                {asuntos.isError
                  ? 'No pude leer tus pendientes.'
                  : asuntos.isPending
                    ? 'Revisando…'
                    : vencidos > 0
                      ? `Tienes ${vencidos} ${vencidos === 1 ? 'cosa vencida' : 'cosas vencidas'} y ${lista.length} abiertas.`
                      : `Tienes ${lista.length} ${lista.length === 1 ? 'cosa abierta' : 'cosas abiertas'}. Nada vencido.`}
              </p>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200">
                <Moon className="h-4 w-4" /> Tu cierre
              </p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                {ahora.getHours() < 18
                  ? 'El cierre del día llega a las 9:00 p. m.'
                  : senales.isError
                    ? 'No pude leer lo que se te escapó hoy.'
                    : `${cuantasSenales} ${cuantasSenales === 1 ? 'cosa se te escapó' : 'cosas se te escaparon'} hoy.`}
              </p>
            </CardBody>
          </Card>
        </div>
      )}

      {/* El hilo se queda con todo el alto que sobre y rueda por dentro: la
          caja de escribir no se mueve de su sitio. */}
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
        {turnos.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">
            Pregúntame lo que quieras sobre tu día.
          </p>
        ) : (
          turnos.map((t, i) => (
            <Burbuja
              key={i}
              t={t}
              onCopiar={(x) => {
                void navigator.clipboard.writeText(x);
                toast.success('Copiado');
              }}
            />
          ))
        )}
        {preguntar.isPending && (
          <div className="flex justify-start">
            <div className="rounded-2xl bg-slate-100 px-3.5 py-2.5 dark:bg-slate-800">
              <Spinner />
            </div>
          </div>
        )}
        <div ref={fondo} />
      </div>

      {turnos.length === 0 && (
        <div className="flex shrink-0 flex-wrap gap-1.5">
          {SUGERENCIAS.map((s) => (
            <button
              key={s}
              onClick={() => enviar(s)}
              className="min-h-[36px] rounded-full bg-slate-100 px-3 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="flex shrink-0 gap-2">
        <input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && enviar(texto)}
          placeholder="Escríbele a Athena…"
          disabled={preguntar.isPending}
          className="min-h-[44px] min-w-0 flex-1 rounded-lg border border-slate-200 px-3 text-base md:text-sm dark:border-slate-700 dark:bg-slate-900"
        />
        <button
          onClick={() => enviar(texto)}
          disabled={!texto.trim() || preguntar.isPending}
          aria-label="Enviar"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-violet-600 text-white disabled:opacity-40"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>

      <p className="shrink-0 px-1 text-xs text-slate-400">
        Athena solo lee y redacta. Nunca manda un WhatsApp: eso lo haces tú.{' '}
        <button
          onClick={() => irA('/inspector/ajustes')}
          className="text-violet-600 underline-offset-2 hover:underline dark:text-violet-300"
        >
          Ajustes
        </button>
      </p>
    </div>
  );
}
